/**
 * Super Admin Home - dashboard overview tailored for SUPER_ADMIN.
 * KPIs, timeline chart, license plan split, critical logs, quick actions.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  DatabaseBackup,
  Key,
  Plus,
  RefreshCw,
  Server,
  Shield,
  ShieldAlert,
  TrendingUp,
} from 'lucide-react';
import { adminAPI, licensesAPI, backendOpsAPI } from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { Sparkline } from '../components/ui/sparkline';
import { packLabel } from '../lib/utils';
import { libelle, nomLisible } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure, formatMontant, formatNombre } from '../lib/formats';

const EMPTY_OBJECT = {};

const fmtXOF = (n) => formatMontant(n ?? 0);

/**
 * Six KPIs used to arrive in six different tinted gradients — peach, blue,
 * white, green, purple, pink — which made the row read as six unrelated
 * things and left no colour free to mark the one that needs attention.
 *
 * They now share the readout surface, and colour is spent only when a value
 * is actually in a bad state.
 */

const PlanDonut = ({ breakdown = EMPTY_OBJECT }) => {
  const entries = Object.entries(breakdown);
  const total = entries.reduce((acc, [, v]) => acc + (v.count || 0), 0);
  if (!total) return <div className="text-sm text-muted-foreground/70">Aucune licence active</div>;

  // The old map was keyed on BASIC/PRO/ENTERPRISE while the API returns pack
  // names (TRANSPORT_DISTRIBUTION, STATIONS_RESEAU, CONTROLE_CARBURANT), so
  // every segment fell through to the same grey and the chart rendered as a
  // flat ring. Assigning from the validated categorical scale by position
  // works whatever the plans are called.
  const PALETTE = [
    'hsl(var(--cat-route))',
    'hsl(var(--cat-hold))',
    'hsl(var(--cat-done))',
    'hsl(var(--process))',
  ];
  let offset = 0;
  const segments = entries.map(([plan, v], i) => {
    const pct = (v.count / total) * 100;
    const seg = {
      plan,
      color: PALETTE[i % PALETTE.length],
      count: v.count,
      pct,
      offset,
    };
    offset += pct;
    return seg;
  });

  const C = 2 * Math.PI * 40;
  return (
    <div className="flex items-center gap-6">
      <svg
        viewBox="0 0 120 120"
        className="h-32 w-32 -rotate-90"
        role="img"
        aria-label={`Répartition des ${total} licences actives par formule.`}
      >
        <circle cx="60" cy="60" r="40" fill="none" stroke="hsl(var(--muted))" strokeWidth="16" />
        {segments.map((s) => (
          <circle
            key={s.plan}
            cx="60"
            cy="60"
            r="40"
            fill="none"
            stroke={s.color}
            strokeWidth="16"
            strokeDasharray={`${(s.pct / 100) * C} ${C}`}
            strokeDashoffset={-(s.offset / 100) * C}
          />
        ))}
      </svg>
      <div className="space-y-1">
        {segments.map((s) => (
          <div key={s.plan} className="flex items-center gap-2 text-sm">
            <span className="inline-block h-3 w-3 rounded" style={{ background: s.color }} />
            <span className="font-medium text-foreground">{packLabel(s.plan)}</span>
            <span className="text-muted-foreground">· {formatNombre(s.count)} ({formatNombre(s.pct)} %)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function SuperAdminHome() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [critical, setCritical] = useState([]);
  const [anomalies, setAnomalies] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ov, tl, m, cr, an, he] = await Promise.allSettled([
        adminAPI.getStatsOverview(),
        adminAPI.getStatsTimeline(30),
        licensesAPI.getMRR(),
        adminAPI.getCriticalLogs(8),
        adminAPI.getAnomalies(24),
        backendOpsAPI.getHealth().catch(() => null),
      ]);
      if (ov.status === 'fulfilled') setOverview(ov.value);
      if (tl.status === 'fulfilled') setTimeline(tl.value);
      if (m.status === 'fulfilled') setMrr(m.value);
      if (cr.status === 'fulfilled') setCritical(cr.value || []);
      if (an.status === 'fulfilled') setAnomalies(an.value);
      if (he.status === 'fulfilled') setHealth(he.value);
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de charger la vue d’ensemble'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const iv = setInterval(loadAll, 60_000);
    return () => clearInterval(iv);
  }, []);

  const tripsSeries = timeline?.trips || [];
  const usersSeries = timeline?.users || [];

  const healthServices = health?.services || {};
  const systemStats = health?.system || {};
  const priorityActions = [
    {
      title: 'Licences à sécuriser',
      value: overview?.licenses?.expired || 0,
      text: 'Renouveler ou suspendre proprement les organisations dont la licence est expirée.',
      action: () => navigate('/licenses'),
      tone: (overview?.licenses?.expired || 0) ? 'rose' : 'emerald',
    },
    {
      title: 'Sécurité des connexions',
      value: anomalies?.failed_logins?.length || 0,
      text: 'Contrôler les connexions refusées et les accès en dehors des heures de travail.',
      action: () => navigate('/audit'),
      tone: (anomalies?.failed_logins?.length || 0) ? 'amber' : 'emerald',
    },
    {
      title: 'État de la plateforme',
      value: health?.status === 'ok' ? 'Bon' : 'À vérifier',
      text: 'Vérifier que la plateforme répond bien et que l’espace disponible est suffisant.',
      action: () => navigate('/backend-ops'),
      tone: health?.status === 'ok' ? 'emerald' : 'rose',
    },
  ];

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Vue d'ensemble de la plateforme"
        description="Organisations, licences, sécurité et état du système."
        stats={[
          {
            label: 'Organisations',
            valeur: overview?.organizations?.active,
            aide: `${overview?.organizations?.total || 0} au total · +${overview?.organizations?.created_last_30d || 0} sur 30 jours`,
            onClick: () => navigate('/organizations'),
          },
          {
            label: 'Utilisateurs',
            valeur: overview?.users?.active,
            aide: `+${overview?.users?.created_last_7d || 0} cette semaine`,
            onClick: () => navigate('/users'),
          },
          {
            label: 'Licences actives',
            valeur: overview?.licenses?.active,
            ton: (overview?.licenses?.expired || 0) ? 'attention' : 'neutre',
            aide: `${overview?.licenses?.total || 0} au total`,
            onClick: () => navigate('/licenses'),
          },
          {
            label: 'Recettes sur 30 jours',
            valeur: overview?.revenue?.last_30d,
            unite: 'FCFA',
            aide: mrr ? `Abonnements par mois : ${fmtXOF(mrr.total_monthly)}` : undefined,
            onClick: () => navigate('/licenses'),
          },
        ]}
        statsPlus={
          <dl className="space-y-1.5 text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Camions</dt>
              <dd className="font-medium tabular-nums">{formatNombre(overview?.trucks?.total ?? 0)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-muted-foreground">Trajets actifs</dt>
              <dd className="font-medium tabular-nums">{formatNombre(overview?.trips?.active ?? 0)}</dd>
            </div>
          </dl>
        }
        actions={
          <>
          <button onClick={loadAll} className="btn-secondary px-3">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
          <button
            onClick={() => navigate('/organizations')}
            className="btn-primary px-3"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle organisation
          </button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        {priorityActions.map((item) => (
          // The card stays neutral and the badge carries the state. Flooding
          // a whole card green to say "nothing to do" spends attention on
          // the items that need none.
          <button
            key={item.title}
            onClick={item.action}
            className="rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-px hover:border-primary/40 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-semibold text-foreground">{item.title}</p>
              <span
                className={`shrink-0 rounded-md border px-2 py-0.5 font-mono text-xs font-bold ${
                  item.tone === 'rose'
                    ? 'border-destructive/25 bg-destructive/10 text-destructive'
                    : item.tone === 'amber'
                      ? 'border-flame/30 bg-flame/10 text-flame-ink'
                      : 'border-success/25 bg-success/10 text-success'
                }`}
              >
                {typeof item.value === 'number' ? formatNombre(item.value) : item.value}
              </span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{item.text}</p>
            <p className="mt-3 text-xs font-semibold text-primary">Ouvrir l’action →</p>
          </button>
        ))}
      </div>

      {/* Évolution et répartition */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="uppercase text-muted-foreground">
                Activité 30 jours
              </h2>
              <p className="text-xs text-muted-foreground/70">
                Nouveaux trajets et nouveaux utilisateurs par jour
              </p>
            </div>
            <TrendingUp className="h-5 w-5 text-success" />
          </div>
          <div className="space-y-2">
            <div>
              <div className="mb-1 text-xs font-semibold text-flame-ink">Trajets</div>
              <Sparkline points={tripsSeries} label="Nouveaux trajets" color="hsl(var(--chart-2))" />
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold text-info">Utilisateurs</div>
              <Sparkline points={usersSeries} label="Nouveaux utilisateurs" color="hsl(var(--chart-1))" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-3">
            <h2 className="uppercase text-muted-foreground">
              Répartition des licences
            </h2>
            <p className="text-xs text-muted-foreground/70">Licences actives par formule</p>
          </div>
          <PlanDonut breakdown={mrr?.by_plan} />
        </div>
      </div>

      {/* Anomalies + Health */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <h2 className="uppercase text-muted-foreground">
                Alertes de sécurité (24 h)
              </h2>
            </div>
            <button
              onClick={() => navigate('/audit')}
              className="text-xs font-semibold text-flame-ink hover:underline"
            >
              Tout voir →
            </button>
          </div>
          {anomalies ? (
            <div className="grid grid-cols-3 gap-3">
              <AnomalyBox
                label="Connexions refusées"
                count={anomalies.failed_logins?.length || 0}
                severity={anomalies.failed_logins?.length ? 'rose' : 'emerald'}
              />
              <AnomalyBox
                label="Hors heures de travail"
                count={anomalies.after_hours_activity?.length || 0}
                severity={anomalies.after_hours_activity?.length ? 'amber' : 'emerald'}
              />
              <AnomalyBox
                label="Suppressions en masse"
                count={anomalies.mass_deletions?.length || 0}
                severity={anomalies.mass_deletions?.length ? 'rose' : 'emerald'}
              />
            </div>
          ) : (
            <div className="text-sm text-muted-foreground/70">Chargement…</div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-info" />
              <h2 className="uppercase text-muted-foreground">
                État du système
              </h2>
            </div>
            <button
              onClick={() => navigate('/backend-ops')}
              className="text-xs font-semibold text-flame-ink hover:underline"
            >
              Ouvrir les opérations techniques →
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <HealthRow label="Base de données" up={healthServices.mongodb?.status === 'up'} detail={healthServices.mongodb?.latency_ms ? `${healthServices.mongodb.latency_ms} ms` : '—'} />
            <HealthRow label="Mémoire rapide" up={healthServices.redis?.status === 'up'} detail={healthServices.redis?.latency_ms ? `${healthServices.redis.latency_ms} ms` : '—'} />
            <HealthRow label="Processeur" up={(systemStats.cpu_percent ?? 0) < 80} detail={systemStats.cpu_percent != null ? `${systemStats.cpu_percent} %` : '—'} />
            <HealthRow label="Mémoire" up={(systemStats.memory_percent ?? 0) < 80} detail={systemStats.memory_percent != null ? `${systemStats.memory_percent} %` : '—'} />
            <HealthRow label="Disque" up={(systemStats.disk_percent ?? 0) < 85} detail={systemStats.disk_percent != null ? `${systemStats.disk_percent} %` : '—'} />
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid gap-4 md:grid-cols-4">
        <QuickAction
          icon={Building2}
          label="Nouvelle organisation"
          onClick={() => navigate('/organizations')}
        />
        <QuickAction
          icon={Key}
          label="Nouvelle licence"
          onClick={() => navigate('/licenses?new=1')}
        />
        <QuickAction
          icon={DatabaseBackup}
          label="Sauvegarder les données"
          onClick={async () => {
            try {
              toast.info('Sauvegarde en cours…');
              await backendOpsAPI.createBackup();
              toast.success('Sauvegarde créée');
            } catch (e) {
              toast.error(messageErreur(e, 'Impossible de créer la sauvegarde'));
            }
          }}
        />
        <QuickAction
          icon={ShieldAlert}
          label="Alertes de sécurité"
          onClick={() => navigate('/audit?preset=critical')}
        />
      </div>

      {/* Critical logs */}
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border/60 p-5">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            <h2 className="uppercase text-muted-foreground">
              Dernières actions sensibles
            </h2>
          </div>
          <button
            onClick={() => navigate('/audit')}
            className="text-xs font-semibold text-flame-ink hover:underline"
          >
            Journal complet →
          </button>
        </div>
        <div className="divide-y divide-border">
          {critical.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground/70">
              Aucune action sensible récente.
            </div>
          )}
          {critical.map((log) => (
            <div key={log.id || log._id || log.timestamp || ''} className="flex items-start justify-between gap-4 p-4 hover:bg-muted/50">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-destructive/10 p-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-readout px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                      {libelle('auditAction', log.action)}
                    </span>
                    <span className="text-sm font-medium text-muted-foreground">
                      {libelle('auditEntity', log.entity_type)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Par <strong>{nomLisible(log.changed_by_name, 'le système')}</strong>
                    {log.organization_name ? ` · ${nomLisible(log.organization_name)}` : ''}
                  </p>
                </div>
              </div>
              <div className="whitespace-nowrap text-xs text-muted-foreground/70">
                {formatDateHeure(log.timestamp)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const AnomalyBox = ({ label, count, severity }) => {
  const colors = {
    rose: 'bg-destructive/10 border-destructive/30 text-destructive',
    amber: 'bg-flame/10 border-flame/30 text-flame-ink',
    emerald: 'bg-success/10 border-success/30 text-success',
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${colors[severity]}`}>
      <div className="kpi-value">{formatNombre(count)}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wide">{label}</div>
    </div>
  );
};

const HealthRow = ({ label, up, detail }) => (
  <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/50 px-3 py-2">
    <div className="flex items-center gap-2">
      <span className={`inline-block h-2 w-2 rounded-full ${up ? 'bg-success' : 'bg-destructive'}`} />
      <span className="font-medium text-muted-foreground">{label}</span>
    </div>
    <span className="font-mono text-xs text-muted-foreground">{detail}</span>
  </div>
);

const QuickAction = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition hover:border-flame/40 hover:shadow"
  >
    <div className="rounded-xl bg-flame/10 p-2 text-flame-ink transition group-hover:bg-flame/15">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="text-sm font-bold text-foreground">{label}</div>
      <div className="text-xs text-muted-foreground/70">Ouvrir</div>
    </div>
  </button>
);
